import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Globe, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-[#1A1A1A] text-white border-b border-[#10dfb2]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            About <span className="text-[#10dfb2]">Funded Lab</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Empowering traders globally to trade with institutional capital
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                We are a global proprietary trading laboratory, 
                dedicated to empowering skilled traders worldwide with access to institutional-grade capital.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                At Funded Lab, we believe trading is both an art and a science. 
                Talented traders shouldn't be constrained by limited resources—so we provide the capital, 
                advanced tools, and data-driven support needed to accelerate their growth.
              </p>
              <p className="text-lg text-gray-600">
               Our platform is engineered for growth: 
               cutting-edge technology, transparent parameters, and rapid payout mechanisms create the perfect chemistry for traders to experiment, refine, and brew their strategies.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1651176118867-f4ac0b1d6da4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx0cmFkaW5nJTIwb2ZmaWNlfGVufDB8fHx8MTc2MjAyODY1NHww&ixlib=rb-4.1.0&q=85"
                alt="Trading Office"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Target className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Transparency
                </h3>
                <p className="text-gray-600">
                  Clear rules, honest communication, and transparent processes at every step.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Users className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Support
                </h3>
                <p className="text-gray-600">
                  On demand support to help you succeed at every stage of your journey.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Globe className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Global Reach
                </h3>
                <p className="text-gray-600">
                  Serving traders from around the world with equal opportunity and support.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Heart className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Impact
                </h3>
                <p className="text-gray-600">
                  Contributing to tree planting initiatives and rebuilding impoverished communities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg"
                alt="Community Development"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Social <span className="text-[#10dfb2]">Impact</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At Funded Lab, we believe in giving back to create a better world. 
                For every trader we fund, we contribute to initiatives that make a real difference.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                <strong className="text-[#10dfb2]">Rebuilding Communities:</strong> We partner with organizations 
                dedicated to rebuilding impoverished countries, providing infrastructure, education, 
                and economic opportunities to communities in need.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                <strong className="text-[#10dfb2]">Environmental Stewardship:</strong> We support global 
                tree planting initiatives to combat climate change, restore ecosystems, and create 
                sustainable futures for generations to come.
              </p>
              <p className="text-lg text-gray-600">
                By trading with Funded Lab, you're not just building your career—you're 
                helping rebuild lives and restore our planet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#10dfb2] to-[#10dfb2] text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join Our Community
          </h2>
          <p className="text-xl mb-8">
            Become part of a global network of successful funded traders
          </p>
          <Link to="/get-funded">
            <Button
              size="lg"
              className="bg-black text-[#10dfb2] hover:bg-gray-900 font-semibold text-lg px-12 py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              Start Your Journey
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
